import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Building2, Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

// Cache de blob URLs por "id|path" para evitar flash al navegar entre páginas.
const _logoCache = new Map();

/**
 * Comprime una imagen en cliente a WebP (máx 1024px lado, calidad 80).
 * Garantiza que nunca pase del límite de 1MB sin importar el tamaño original.
 */
async function compressImage(file) {
  // SVG: no se puede comprimir con canvas, se envía tal cual
  if (file.type === "image/svg+xml") return file;
  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result); fr.onerror = rej;
    fr.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im); im.onerror = rej;
    im.src = dataUrl;
  });
  const MAX = 1024;
  let { width, height } = img;
  if (width > MAX || height > MAX) {
    const r = Math.min(MAX / width, MAX / height);
    width = Math.round(width * r); height = Math.round(height * r);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/webp", 0.82));
  if (!blob || blob.size >= file.size) return file; // si no se ganó espacio, usar original
  return new File([blob], (file.name || "logo").replace(/\.[^.]+$/, "") + ".webp",
                   { type: "image/webp", lastModified: Date.now() });
}

export default function MandanteLogo({ mandante, size = "md", editable = false, onChanged }) {
  const confirmAction = useConfirm();
  const [uploading, setUploading] = useState(false);
  const cacheKey = mandante?.id && mandante?.logo_path ? `${mandante.id}|${mandante.logo_path}` : null;
  const [objectUrl, setObjectUrl] = useState(() => (cacheKey ? _logoCache.get(cacheKey) || null : null));
  const [reloadKey, setReloadKey] = useState(0);
  const [hovering, setHovering] = useState(false);
  const inputRef = useRef(null);
  const px = { xs: 24, sm: 32, md: 48, lg: 72 }[size] || 48;
  const iconSize = Math.round(px * 0.55);
  const hasPath = !!mandante?.logo_path;
  const hasLogo = hasPath && !!objectUrl;
  const loading = hasPath && !objectUrl;
  const boxStyle = hasLogo
    ? { height: px, maxWidth: px * 2.5, minWidth: px * 0.6 }
    : { width: px, height: px };

  useEffect(() => {
    if (!cacheKey) { setObjectUrl(null); return; }
    const cached = _logoCache.get(cacheKey);
    if (cached) { setObjectUrl(cached); return; }
    let cancelled = false;
    api.get(`/mandantes/${mandante.id}/logo`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        _logoCache.set(cacheKey, url);
        setObjectUrl(url);
      })
      .catch(() => { if (!cancelled) setObjectUrl(null); });
    return () => { cancelled = true; };
  }, [cacheKey, reloadKey]);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type) && file.type !== "image/svg+xml") {
      toast.error("Solo imágenes (PNG, JPG, WEBP, SVG)");
      return;
    }
    setUploading(true);
    try {
      // Compresión cliente: 1024px máx + WebP 82%. Nunca sobrepasa el límite backend.
      let f = file;
      try { f = await compressImage(file); } catch (_) { /* usar original si falla */ }
      if (f.size > 1024 * 1024) {
        toast.error(`La imagen pesa ${(f.size/1024/1024).toFixed(1)} MB después de comprimir. Usa una más chica.`);
        return;
      }
      const fd = new FormData(); fd.append("file", f, f.name || "logo.webp");
      const { data } = await api.post(`/mandantes/${mandante.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Invalidar cache del path anterior y forzar refetch del nuevo
      if (cacheKey) {
        const old = _logoCache.get(cacheKey);
        if (old) URL.revokeObjectURL(old);
        _logoCache.delete(cacheKey);
      }
      setObjectUrl(null);
      onChanged?.({ ...mandante, logo_path: data.logo_path });
      toast.success("Logo actualizado");
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo subir"); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    const ok = await confirmAction({ title: "Eliminar logo", description: "¿Quitar el logo de este mandante?", confirmText: "Eliminar", variant: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/mandantes/${mandante.id}/logo`);
      if (cacheKey) {
        const old = _logoCache.get(cacheKey);
        if (old) URL.revokeObjectURL(old);
        _logoCache.delete(cacheKey);
      }
      setObjectUrl(null);
      onChanged?.({ ...mandante, logo_path: null });
      toast.success("Logo eliminado");
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="relative inline-block"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}>
      <div style={boxStyle}
        className="shrink-0 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
        {uploading || loading ? (
          <div className="w-full h-full flex items-center justify-center bg-slate-50">
            <Loader2 className="animate-spin text-slate-300" size={iconSize} />
          </div>
        ) : hasLogo ? (
          <img data-testid={`mandante-logo-${mandante.id}`} src={objectUrl} alt={mandante.name}
            style={{ height: "100%", width: "auto", maxWidth: "100%" }}
            className="object-contain" />
        ) : (
          <div className="bg-indigo-50 text-indigo-600 w-full h-full flex items-center justify-center">
            <Building2 size={iconSize} />
          </div>
        )}
      </div>
      {editable && (
        <>
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          {!uploading && (hovering || !hasLogo) && (
            <div className={`absolute inset-0 rounded-xl flex items-center justify-center gap-1.5 transition-opacity ${hasLogo ? "bg-black/55" : "bg-black/0"}`}>
              <button data-testid={`upload-logo-${mandante.id}`} onClick={() => inputRef.current?.click()}
                className={`p-1.5 rounded-full shadow-sm ${hasLogo ? "bg-white/90 hover:bg-white text-slate-700 hover:text-indigo-600" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}
                title={hasPath ? "Cambiar logo" : "Subir logo"}>
                <Upload size={size === "lg" ? 16 : 12} />
              </button>
              {hasPath && (
                <button data-testid={`del-logo-${mandante.id}`} onClick={remove}
                  className="p-1.5 rounded-full bg-white/90 hover:bg-white text-slate-700 hover:text-red-600 shadow-sm"
                  title="Eliminar logo">
                  <Trash2 size={size === "lg" ? 16 : 12} />
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
